import { Navigate, Route, Routes } from "react-router-dom";
import { signOut, useSession } from "@otto/plattform";
import { Shell } from "./shared/components/Shell.js";
import { Page } from "./shared/components/Page.js";
import { SignIn } from "./features/auth/components/SignIn.js";
import { FingersAndKeyboard } from "./features/settings/components/FingersAndKeyboard.js";
import { Assessment } from "./features/settings/components/Assessment.js";
import { TitleAndStars } from "./features/settings/components/TitleAndStars.js";
import { LessonDetail } from "./features/lessons/components/LessonDetail.js";
import { FirstLesson } from "./features/lessons/components/FirstLesson.js";
import { NewLessonWizard } from "./features/lessons/components/NewLessonWizard.js";
import { Workspace } from "./features/draft/components/Workspace.js";
import { Versions } from "./features/versions/components/Versions.js";
import { CourseLayout } from "./features/draft/components/CourseLayout.js";
import { Overview } from "./features/draft/components/Overview.js";
import { CourseList } from "./features/curricula/components/CourseList.js";
import { CourseSettings } from "./features/curricula/components/CourseSettings.js";
import { COURSES } from "./shared/lib/coursePath.js";
import { Schools } from "./features/schools/components/Schools.js";
import { TourProvider } from "./shared/context/TourContext.js";
import { SchoolDetail } from "./features/schools/components/SchoolDetail.js";
import { Billing } from "./features/schools/components/Billing.js";
import styles from "./shared/components/shell.module.css";

export function App() {
  const { client, state } = useSession();

  if (state.kind === "loading") return <p className={styles.empty}>Einen Moment …</p>;
  if (state.kind === "signedOut") return <SignIn />;

  // Angemeldet, aber die Rechte waren nicht zu ermitteln – Netz, Uhrendrift,
  // Server kurz weg. Das ist ausdrücklich NICHT „kein Zugang“: wer das
  // verwechselt, schickt eine berechtigte Nutzerin zur Schulleitung, obwohl sie
  // nur einmal neu laden müsste.
  if (state.kind === "failed") {
    return (
      <div className={styles.centered}>
        <div className={styles.signInCard}>
          <h1 className={styles.title}>Gerade nicht erreichbar</h1>
          <p className={styles.subtitle}>
            Die Anmeldung hat geklappt, aber Ihre Berechtigung war nicht abzurufen. Das
            ist kein Problem mit Ihrem Konto – meist genügt ein erneuter Versuch.
          </p>
          <button
            type="button"
            className={styles.button}
            onClick={() => window.location.reload()}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  // Angemeldet, aber ohne Plattformrecht. Der Fall ist wichtig: dieselbe
  // E-Mail-Domäne, dieselben Magic Links - nur die Berechtigung fehlt. Eine
  // leere Oberfläche wäre hier das Verwirrendste.
  if (!state.identity.isEditorial) {
    return (
      <div className={styles.centered}>
        <div className={styles.signInCard}>
          <h1 className={styles.title}>Kein Plattformzugang</h1>
          <p className={styles.subtitle}>
            Das Konto {state.identity.user.email} ist angemeldet, hat aber kein Recht auf
            der Plattform. Klassen und Ergebnisse liegen in der Verwaltung, nicht hier.
          </p>
          <button
            type="button"
            className={styles.buttonQuiet}
            onClick={() => void signOut(client)}
          >
            Abmelden
          </button>
        </div>
      </div>
    );
  }

  // Der Entwurfszustand liegt jetzt IM Lehrgang, nicht über der ganzen App.
  //
  // Vorher umschloss er beide Bereiche und arbeitete nur für einen; die Grenze zu
  // "Schulen" hielt allein die Zusage, dass dort niemand den Kontext anfasst.
  // Seit der Lehrgang in der Adresse steht, gibt es einen besseren Ort: das
  // Layout unter `/lehrgaenge/:slug`. Dort ist auch klar, WELCHER Entwurf gemeint
  // ist - und ein Wechsel des Lehrgangs wirft den vorigen Zustand weg, statt ihn
  // stillschweigend mitzuschleppen.
  return (
    // Die Führung liegt UM die Routen: sie beginnt auf /lehrgaenge, geht in
    // einen Lehrgang hinein und darf dabei nicht verloren gehen. Und sie liegt
    // hinter den Zugangsfällen darüber – ein Konto ohne Plattformrecht sieht
    // hier eine Erklärung und keine Arbeitsfläche, also auch keine Führung.
    <TourProvider userId={state.identity.user.id}>
      <Routes>
        <Route element={<Shell />}>
          {/* Eine Liste ist keine Arbeitsfläche: die Lehrgangsliste bekommt die
            Arbeitsbreite, die Arbeitsfläche darunter die weite. */}
          <Route
            path={COURSES}
            element={
              <Page>
                <CourseList />
              </Page>
            }
          />

          <Route
            path={`${COURSES}/:slug`}
            element={
              <Page>
                <CourseLayout />
              </Page>
            }
          >
            <Route index element={<Navigate to="uebersicht" replace />} />

            {/* Der Assistent steht VOR der Arbeitsfläche und außerhalb von ihr:
              er füllt die Seite allein. Die Liste daneben führte zu einer
              Lektion, die es noch nicht gibt. */}
            <Route path="lektionen/neu" element={<NewLessonWizard />} />

            {/* Alles andere teilt sich die Liste links. Sie ist der einzige Ort
              für die Wahl - eine Unternavigation darüber bot dieselben Ziele
              ein zweites Mal an. */}
            <Route element={<Workspace />}>
              <Route path="uebersicht" element={<Overview />} />
              <Route path="stammdaten" element={<CourseSettings />} />
              <Route path="fassungen" element={<Versions />} />
              <Route path="lektionen" element={<FirstLesson />} />
              <Route path="lektionen/:lessonKey" element={<LessonDetail />} />
              <Route path="grundlagen" element={<Navigate to="tastatur" replace />} />
              <Route path="grundlagen/tastatur" element={<FingersAndKeyboard />} />
              <Route path="grundlagen/sterne" element={<TitleAndStars />} />
              <Route path="grundlagen/test" element={<Assessment />} />
            </Route>
          </Route>

          {/* Die feste Adresse steht vor der veränderlichen: sonst hielte
            React Router "billing" für eine Schulkennung. */}
          <Route
            path="/schools/billing"
            element={
              <Page>
                <Billing />
              </Page>
            }
          />
          <Route
            path="/schools/:schoolId"
            element={
              <Page>
                <SchoolDetail />
              </Page>
            }
          />
          <Route
            path="/schools"
            element={
              <Page>
                <Schools />
              </Page>
            }
          />

          {/* Alte Adressen. Sie bleiben, weil die Redaktion Lesezeichen hat -
            ein toter Link auf die eigene Arbeitsfläche ist ein Fehler, den
            niemand meldet und jeder umgeht. Wohin genau, weiß hier niemand mehr:
            der Lehrgang stand in keiner dieser Adressen. Die Liste ist die
            ehrliche Antwort. */}
          <Route path="/lehrgang/*" element={<Navigate to={COURSES} replace />} />
          <Route path="/settings" element={<Navigate to={COURSES} replace />} />
          <Route path="/lessons" element={<Navigate to={COURSES} replace />} />
          <Route path="/releases" element={<Navigate to={COURSES} replace />} />

          <Route path="*" element={<Navigate to={COURSES} replace />} />
        </Route>
      </Routes>
    </TourProvider>
  );
}
